import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { reachGoal } from '../services/metrica';
import './TiktokPage.css';

const TOTAL_ROUNDS = 3;
const ANSWER_TIME = 15;
const AUTO_NEXT_MS = 4000;

const EMOJI_MAP = [
  { threshold: 9, emoji: '🤩', label: 'ЛЕГЕНДА' },
  { threshold: 7, emoji: '😎', label: 'ОГОНЬ' },
  { threshold: 5, emoji: '😐', label: 'НОРМ' },
  { threshold: 3, emoji: '😰', label: 'КРИНЖ' },
  { threshold: 0, emoji: '💀', label: 'ПОЛНЫЙ КРИНЖ' },
];

const getEmoji = (score) => EMOJI_MAP.find(e => score >= e.threshold) || EMOJI_MAP[EMOJI_MAP.length - 1];

const TiktokPage = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('intro');
  const [round, setRound] = useState(0);
  const [scores, setScores] = useState([]);
  const [situations, setSituations] = useState([]);
  const [currentSituation, setCurrentSituation] = useState(null);
  const [answer, setAnswer] = useState('');
  const [grade, setGrade] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [timer, setTimer] = useState(ANSWER_TIME);
  const [showShake, setShowShake] = useState(false);
  const [listening, setListening] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const timerRef = useRef(null);
  const autoNextRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);

  // Load situations with audio
  useEffect(() => {
    fetch('/tiktok/situations_top20.json')
      .then(r => { if (r.ok) return r.json(); return null; })
      .then(data => {
        if (data) {
          const shuffled = [...data].sort(() => Math.random() - 0.5);
          setSituations(shuffled);
        }
        setAudioReady(true);
      })
      .catch(() => setAudioReady(true));
  }, []);

  // Web Speech Recognition
  const initRecognition = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return null;
    const rec = new SR();
    rec.lang = 'ru-RU';
    rec.continuous = false;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let t = '';
      for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
      setAnswer(t);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    return rec;
  }, []);

  const toggleMic = () => {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      // Auto-submit after stopping mic
      setTimeout(() => {
        const finalAnswer = inputRef.current?.value || answer;
        if (finalAnswer.trim().length >= 2) handleSubmit(false);
      }, 300);
      return;
    }
    const rec = recognitionRef.current || initRecognition();
    if (!rec) { alert('Открой в Chrome на Android'); return; }
    recognitionRef.current = rec;
    setAnswer('');
    setListening(true);
    rec.start();
  };

  // Quick SFX player (no ducking, fire and forget)
  const playSfx = (file) => {
    const audio = new Audio(`/tiktok/audio/${file}`);
    audio.volume = 0.7;
    audio.play().catch(() => {});
  };

  // Audio playback with ducking
  const playAudio = (text, audioFile) => {
    return new Promise((resolve) => {
      const bg = audioRef.current;
      if (bg) bg.volume = 0.05;
      if (audioFile) {
        const audio = new Audio(`/tiktok/audio/${audioFile}`);
        audio.volume = 0.9;
        audio.onended = () => { if (bg) bg.volume = 0.2; resolve(); };
        audio.onerror = () => { if (bg) bg.volume = 0.2; resolve(); };
        audio.play().catch(() => { if (bg) bg.volume = 0.2; resolve(); });
      } else { resolve(); }
    });
  };

  const speakTTS = (text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text.replace(/[*#_\[\]()]/g, ''));
    u.lang = 'ru-RU'; u.rate = 1.0;
    window.speechSynthesis.speak(u);
  };

  // ── Start game ───────────────────────────
  const startGame = async () => {
    reachGoal('tiktok_start');
    setScores([]);
    setRound(0);
    setPhase('playing');
    if (audioRef.current) {
      audioRef.current.volume = 0.2;
      audioRef.current.play().catch(() => {});
    }
    await loadSituation(0);
  };

  const loadSituation = async (roundIdx) => {
    setAnswer('');
    setGrade(null);
    setEvaluating(false);
    setTimer(ANSWER_TIME);
    if (autoNextRef.current) clearTimeout(autoNextRef.current);

    const sit = situations[roundIdx % situations.length];
    setCurrentSituation(sit);

    // Play round intro + situation audio
    playSfx('sfx_bell.mp3');
    await playAudio(`Раунд ${roundIdx + 1}`, `r${roundIdx + 1}.mp3`);
    await playAudio(sit.text, `s${sit.id}.mp3`);

    // Start timer AFTER audio
    startTimer();
  };

  const startTimer = () => {
    setTimer(ANSWER_TIME);
    let tickCount = 0;
    timerRef.current = setInterval(() => {
      setTimer(t => {
        const next = t - 1;
        if (next <= 5 && next > 0) playSfx('sfx_tick.mp3');
        if (next <= 0) { clearInterval(timerRef.current); return 0; }
        return next;
      });
    }, 1000);
  };

  // Auto-submit at 0
  useEffect(() => {
    if (phase === 'playing' && timer === 0 && !grade && !evaluating) {
      handleSubmit(true);
    }
  }, [timer, phase, grade, evaluating]);

  const handleSubmit = async (timeout = false) => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recognitionRef.current && listening) { recognitionRef.current.stop(); setListening(false); }
    if (!timeout && answer.trim().length < 2) return;

    setEvaluating(true);
    reachGoal('tiktok_answer');

    if (timeout) { playSfx('sfx_buzz.mp3'); await playAudio('Время вышло!', 'time_up.mp3'); }

    try {
      const res = await fetch('/api/demo-answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ situation: currentSituation.text, answer: timeout ? 'не знаю' : answer.trim() }),
      });
      const data = await res.json();
      const score = data.score || 5;
      const cringe = data.cringe || 5;
      const comment = data.comment || '';

      setGrade({ score, cringe, comment });
      setScores(prev => [...prev, score]);
      setEvaluating(false);

      // SFX based on score
      // SFX + Jane voice for exact score
      let gradeAudio = `grade_${score}.mp3`;
      if (score >= 8) playSfx('sfx_high.mp3');
      else if (score <= 3) { playSfx('sfx_low.mp3'); setShowShake(true); setTimeout(() => setShowShake(false), 600); }
      else playSfx('sfx_pop.mp3');
      setTimeout(() => playAudio('', gradeAudio), 600);

      // Auto-next after delay
      autoNextRef.current = setTimeout(() => nextRound(), AUTO_NEXT_MS);
    } catch (e) {
      setGrade({ score: 5, cringe: 5, comment: 'Сервер устал' });
      setScores(prev => [...prev, 5]);
      setEvaluating(false);
      autoNextRef.current = setTimeout(() => nextRound(), AUTO_NEXT_MS);
    }
  };

  const nextRound = () => {
    if (autoNextRef.current) clearTimeout(autoNextRef.current);
    playSfx('sfx_whoosh.mp3');
    if (round + 1 >= TOTAL_ROUNDS) {
      reachGoal('tiktok_finish');
      setPhase('final');
      playAudio('Игра окончена!', 'final.mp3');
    } else {
      setRound(r => r + 1);
      loadSituation(round + 1);
    }
  };

  const totalScore = scores.reduce((a, b) => a + b, 0);
  const maxScore = TOTAL_ROUNDS * 10;
  const currentGrade = grade ? getEmoji(grade.score) : null;
  const micSupported = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  // ── INTRO ────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="tt-page">
        <Helmet><title>Бой с кринжем — TikTok режим</title></Helmet>
        <div className="tt-intro">
          <div className="tt-intro-logo">⚡</div>
          <h1 className="tt-intro-title">БОЙ С КРИНЖЕМ</h1>
          <p className="tt-intro-sub">3 раунда · {ANSWER_TIME} секунд на ответ<br/>Озвучка + голосовой ввод 🎤</p>
          <div className="tt-intro-stats">
            <span>{TOTAL_ROUNDS} раунда</span>
            <span>⏱ {ANSWER_TIME} сек</span>
            <span>макс {maxScore} баллов</span>
          </div>
          {micSupported && <p className="tt-mic-hint">🎤 Можно отвечать голосом!</p>}
          <button className="tt-btn-go" onClick={startGame} disabled={!audioReady || situations.length === 0}>
            ПОЕХАЛИ 🔥
          </button>
          <button className="tt-btn-back" onClick={() => navigate('/')}>← на главную</button>
        </div>
        <audio ref={audioRef} src="/tiktok/audio/bg.mp3" loop preload="auto" />
      </div>
    );
  }

  // ── FINAL ────────────────────────────────
  if (phase === 'final') {
    const avg = totalScore / TOTAL_ROUNDS;
    const finalEmoji = getEmoji(Math.round(avg));
    return (
      <div className="tt-page">
        <div className="tt-final">
          <div className="tt-final-emoji">{finalEmoji.emoji}</div>
          <div className="tt-final-label">{finalEmoji.label}</div>
          <div className="tt-final-score">
            <span className="tt-final-num">{totalScore}</span>
            <span className="tt-final-max">/{maxScore}</span>
          </div>
          <div className="tt-final-rounds">
            {scores.map((s, i) => (
              <div key={i} className="tt-round-chip">
                <span className="tt-round-num">Р{i + 1}</span>
                <span className="tt-round-score">{s}/10</span>
              </div>
            ))}
          </div>
          <p className="tt-final-share">📸 Заскринь и выложи в TikTok!</p>
          <div className="tt-final-btns">
            <button className="tt-btn-go" onClick={startGame}>ЕЩЁ РАЗ 🔥</button>
            <button className="tt-btn-back" onClick={() => navigate('/')}>← на главную</button>
          </div>
        </div>
        <audio ref={audioRef} src="/tiktok/audio/bg.mp3" loop preload="auto" />
      </div>
    );
  }

  // ── PLAYING / GRADE ──────────────────────
  return (
    <div className={`tt-page ${showShake ? 'shake' : ''}`}>
      <div className="tt-game">
        <audio ref={audioRef} src="/tiktok/audio/bg.mp3" loop preload="auto" />

        {/* Top bar */}
        <div className="tt-topbar">
          <div className="tt-round-indicator">
            {[...Array(TOTAL_ROUNDS)].map((_, i) => (
              <div key={i} className={`tt-dot ${i < round ? 'done' : i === round ? 'active' : ''}`}>
                {i < round && scores[i] !== undefined ? scores[i] : ''}
              </div>
            ))}
          </div>
          <div className="tt-total-chip">🔥 {totalScore}</div>
        </div>

        {/* Situation */}
        <div className="tt-situation-wrap tt-phase" key={`sit-${round}-${currentSituation?.id}`}>
          <div className="tt-situation-label">КРИНЖ #{round + 1}</div>
          <div className="tt-situation-text">
            {currentSituation?.text || '...'}
          </div>
        </div>

        {/* Timer + Input OR Grade */}
        {!grade ? (
          <>
            <div className={`tt-timer ${timer <= 5 ? 'urgent' : ''} ${evaluating ? 'hidden' : ''}`}>
              <div className="tt-timer-bar">
                <div className="tt-timer-fill" style={{ width: `${(timer / ANSWER_TIME) * 100}%` }} />
              </div>
              <div className="tt-timer-num">{timer}</div>
            </div>

            {evaluating ? (
              <div className="tt-evaluating">
                <div className="tt-eval-spinner" />
                <p className="tt-eval-text">Jane оценивает...</p>
              </div>
            ) : (
              <div className="tt-input-area">
                <textarea
                  ref={inputRef}
                  className={`tt-input ${listening ? 'listening' : ''}`}
                  placeholder={listening ? '🎤 Слушаю... нажми ⏹ чтобы ответить' : micSupported ? 'Голосом ⏹ или текстом...' : 'Как выкрутишься?'}
                  value={answer}
                  onChange={e => setAnswer(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
                  }}
                  maxLength={200}
                  rows={2}
                  disabled={timer === 0}
                  autoFocus
                />
                <div className="tt-input-btns">
                  {micSupported ? (
                    <button className={`tt-mic ${listening ? 'recording' : ''}`} onClick={toggleMic} disabled={timer === 0}>
                      {listening ? '⏹' : '🎤'}
                    </button>
                  ) : (
                    <button className="tt-send" onClick={() => handleSubmit()} disabled={answer.trim().length < 2 || timer === 0}>
                      GO!
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="tt-grade tt-phase" key={`grade-${round}-${grade.score}`}>
            <div className={`tt-grade-emoji tt-pop`}>{currentGrade.emoji}</div>
            <div className="tt-grade-score">
              <span className="tt-grade-num">{grade.score}</span>
              <span className="tt-grade-max">/10</span>
            </div>
            <div className="tt-grade-label">{currentGrade.label}</div>
            <div className="tt-grade-shame">
              <span className="tt-shame-label">КРИНЖ</span>
              <div className="tt-shame-bar">
                <div className={`tt-shame-fill ${grade.cringe >= 7 ? 'high' : grade.cringe >= 4 ? 'mid' : 'low'}`}
                  style={{ width: `${grade.cringe * 10}%` }} />
              </div>
              <span className="tt-shame-val">{grade.cringe}/10</span>
            </div>
            {grade.comment && <p className="tt-grade-comment">{grade.comment.substring(0, 80)}</p>}
            <div className="tt-auto-next-bar">
              <div className="tt-auto-next-fill" />
            </div>
            <button className="tt-next-btn" onClick={nextRound}>
              {round + 1 >= TOTAL_ROUNDS ? 'ИТОГ 🏆' : 'ДАЛЕЕ →'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TiktokPage;
